import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button, CopyButton, type ResultState } from "./ui";
import { formatDate } from "./format";
import { normalizeAccessToken } from "./acCheck";
import { parseJsonResponse, messageFromData } from "./http";
import { extractItems, historyRowsFromResult, mergeHistoryByCdkey, mergeHistoryByTaskId, type HistoryItem } from "./redeemHistory";
import {
  externalRedeemProgressText,
  externalRedeemProgressValue,
  externalRedeemTerminal,
  externalRedeemRowLabel,
  externalRedeemRowMessage,
  externalRedeemRowStatus
} from "./redeemExternal";

type Mode = "single" | "batch" | "status";
type QueueSummary = { vip_queue_count: number; normal_queue_count: number };
type PollState = { checking: boolean; lastCheckedAt?: string; error?: string };

const emptyResult: ResultState = { loading: false };
const emptyQueue: QueueSummary = { vip_queue_count: 0, normal_queue_count: 0 };
const emptyPollState: PollState = { checking: false };
const historyStorageKey = "ai-hqdk-redeem-history";

function extractQueue(data: unknown): QueueSummary | null {
  if (!data || typeof data !== "object") return null;
  const root = data as Record<string, unknown>;
  const source = root.data && typeof root.data === "object" ? (root.data as Record<string, unknown>) : root;
  const vip = Number(source.vip_queue_count);
  const normal = Number(source.normal_queue_count);
  if (!Number.isFinite(vip) || !Number.isFinite(normal)) return null;
  return { vip_queue_count: vip, normal_queue_count: normal };
}

function validateUnique(values: string[]) {
  const seen = new Set<string>();
  for (const value of values) {
    const key = value.toLowerCase();
    if (seen.has(key)) return value;
    seen.add(key);
  }
  return "";
}

function minutesSince(value: string) {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return 0;
  return Math.max(0, Math.floor((Date.now() - time) / 60000));
}

function uniqueStrings(values: Array<string | undefined>) {
  return Array.from(new Set(values.map((value) => String(value || "").trim()).filter(Boolean)));
}

function loadHistory(): HistoryItem[] {
  try {
    const raw = window.localStorage.getItem(historyStorageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: HistoryItem[]) {
  window.localStorage.setItem(historyStorageKey, JSON.stringify(history.slice(0, 200)));
}

function QueueCards({ queue, loading, error }: { queue: QueueSummary; loading: boolean; error?: string }) {
  return (
    <div className="queueWrap">
      <div className="queueCard vip">
        <span>VIP 通道</span>
        <strong>{loading ? "--" : queue.vip_queue_count}</strong>
        <small>优先兑换队列</small>
      </div>
      <div className="queueCard">
        <span>普通通道</span>
        <strong>{loading ? "--" : queue.normal_queue_count}</strong>
        <small>{error ? "队列暂不可用" : "正在等待系统接单"}</small>
      </div>
    </div>
  );
}

function HistoryPanel({ history }: { history: HistoryItem[] }) {
  return (
    <section className="panel historyPanel">
      <div className="historyHead">
        <div>
          <h2>历史轨迹</h2>
          <p>本地保存最近 200 条兑换记录，便于快速追踪任务状态。</p>
        </div>
      </div>
      {history.length ? (
        <div className="historyList">
          {history.map((item) => (
            <div key={`${item.cdkey}-${item.updated_at}`} className="historyItem">
              <div>
                <strong>
                  <code>{item.cdkey}</code>
                </strong>
                <p>{item.message || item.display_status || item.status}</p>
              </div>
              <div className="historyMeta">
                <span className={`badge ${item.status}`}>{item.display_status || item.status}</span>
                <small>{item.task_id || "-"}</small>
                <small>{formatDate(item.updated_at)}</small>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="historyEmpty">
          <strong>暂无历史记录</strong>
          <p>提交一次兑换或查询后，这里会自动保存并更新。</p>
        </div>
      )}
    </section>
  );
}

function ResultView({ result }: { result: ResultState }) {
  const items = useMemo(() => extractItems(result.data), [result.data]);
  if (result.loading) return <div className="redeemResult muted">正在处理...</div>;
  if (result.error) return <div className="redeemResult error">{result.error}</div>;
  if (!result.data) return null;

  const notice = messageFromData(result.data, "请求完成");
  return (
    <div className="redeemResult">
      <div className="resultTop">
        <strong>处理结果</strong>
        <CopyButton value={JSON.stringify(result.data, null, 2)} label="复制 JSON" />
      </div>
      <p className="notice">{notice}</p>
      {items.length ? (
        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th>CDKey</th>
                <th>状态</th>
                <th>信息</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const row = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
                const status = externalRedeemRowStatus(row);
                return (
                  <tr key={index}>
                    <td>{String(row.cdkey || row.key || "-")}</td>
                    <td>
                      <span className={`badge ${status}`}>{externalRedeemRowLabel(row)}</span>
                    </td>
                    <td>{externalRedeemRowMessage(row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="notice">{notice}</p>
      )}
      <details>
        <summary>原始响应</summary>
        <pre>{JSON.stringify(result.data, null, 2)}</pre>
      </details>
    </div>
  );
}

function ProgressPanel({ history, pollState }: { history: HistoryItem[]; pollState: PollState }) {
  const activeItems = history
    .filter((item) => !externalRedeemTerminal(item.status))
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))
    .slice(0, 6);

  if (!activeItems.length) return null;

  return (
    <section className="panel progressPanel">
      <div className="historyHead">
        <div>
          <h2>兑换进度</h2>
          <p>自动轮询未完成任务，状态变化后会同步更新。</p>
        </div>
        <div className="progressSummary">
          <span className={`badge ${pollState.error ? "failed" : pollState.checking ? "running" : "success"}`}>
            {pollState.error ? "自动刷新异常" : pollState.checking ? "正在刷新" : "自动刷新中"}
          </span>
          <small>{pollState.lastCheckedAt ? `最近检查 ${formatDate(pollState.lastCheckedAt)}` : "等待首次检查"}</small>
        </div>
      </div>
      <div className="progressList">
        {activeItems.map((item) => {
          const value = externalRedeemProgressValue(item.status);
          const staleMinutes = minutesSince(item.updated_at);
          const stalled = (item.status === "pending_dispatch" || item.status === "running" || item.status === "dispatched") && staleMinutes >= 2;
          return (
            <div key={`${item.cdkey}-${item.updated_at}`} className="progressCard">
              <div className="progressCardHead">
                <div>
                  <strong>{item.cdkey}</strong>
                  <p>{externalRedeemProgressText(item.status)}</p>
                </div>
                <span className={`badge ${item.status}`}>{item.display_status || externalRedeemRowLabel(item)}</span>
              </div>
              <div className="progressTrack" aria-label="兑换进度">
                <div className={`progressFill ${item.status}`} style={{ width: `${value}%` }} />
              </div>
              <div className="progressStats">
                <small>当前进度 {value}%</small>
                {stalled ? <small className="progressWarn">状态已停留 {staleMinutes} 分钟，可能仍在排队或等待后端处理</small> : null}
              </div>
              <div className="progressMeta">
                <small>{item.task_id || "任务创建中"}</small>
                <small>{formatDate(item.updated_at)}</small>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function RedeemPage() {
  const [mode, setMode] = useState<Mode>("single");
  const [cdkey, setCdkey] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [batch, setBatch] = useState("");
  const [statusKeys, setStatusKeys] = useState("");
  const [result, setResult] = useState<ResultState>(emptyResult);
  const [queue, setQueue] = useState<QueueSummary>(emptyQueue);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string>();
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [pollState, setPollState] = useState<PollState>(emptyPollState);

  useEffect(() => {
    document.title = "AI华强北 · CDK 兑换";
    setHistory(loadHistory());
  }, []);

  useEffect(() => {
    saveHistory(history);
  }, [history]);

  useEffect(() => {
    loadQueue();
    const timer = window.setInterval(() => loadQueue(false), 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const pending = history.filter((item) => !externalRedeemTerminal(item.status));
    if (!pending.length) return;

    const refresh = async () => {
      const startedAt = new Date().toISOString();
      const taskIds = uniqueStrings(pending.map((item) => item.task_id));
      const cdkeys = uniqueStrings(pending.map((item) => item.cdkey));
      setPollState((current) => ({ ...current, checking: true, error: undefined }));
      let hasSuccess = false;

      try {
        if (taskIds.length) {
          const response = await fetch("/redeem-api/redeem-tasks/status", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ task_ids: taskIds })
          });
          const data = await parseJsonResponse(response);
          if (response.ok) {
            applyHistory(data);
            hasSuccess = true;
          }
        }

        if (cdkeys.length) {
          const response = await fetch("/redeem-api/cdkey-redeems/status", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ cdkeys })
          });
          const data = await parseJsonResponse(response);
          if (response.ok) {
            applyHistory(data);
            hasSuccess = true;
          }
        }
        setPollState({
          checking: false,
          lastCheckedAt: startedAt,
          error: hasSuccess ? undefined : "状态接口暂无新数据"
        });
      } catch {
        setPollState({
          checking: false,
          lastCheckedAt: startedAt,
          error: "状态刷新失败，请稍后再试"
        });
      }
    };

    refresh();
    const timer = window.setInterval(refresh, 5000);
    return () => window.clearInterval(timer);
  }, [history]);

  async function loadQueue(showLoading = true) {
    if (showLoading) setQueueLoading(true);
    try {
      const response = await fetch("/redeem-api/queue-summary");
      const data = await parseJsonResponse(response);
      const parsed = extractQueue(data);
      if (!response.ok || !parsed) throw new Error(messageFromData(data, "队列读取失败"));
      setQueue(parsed);
      setQueueError(undefined);
    } catch (error) {
      setQueueError(error instanceof Error ? error.message : "队列读取失败");
    } finally {
      if (showLoading) setQueueLoading(false);
    }
  }

  function applyHistory(data: unknown) {
    const now = new Date().toISOString();
    setHistory((current) => {
      const byCdkey = mergeHistoryByCdkey(current, data, now);
      return mergeHistoryByTaskId(byCdkey, data, now).slice(0, 200);
    });
    const rows = historyRowsFromResult(data);
    if (!rows.length) return;
    setHistory((current) => {
      const seen = new Map(current.map((item) => [item.cdkey.toLowerCase(), item]));
      for (const row of rows) {
        seen.set(row.cdkey.toLowerCase(), row);
      }
      return Array.from(seen.values()).sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1)).slice(0, 200);
    });
  }

  async function submit(path: string, payload: unknown) {
    setResult({ loading: true });
    try {
      const response = await fetch(path, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonResponse(response);
      setResult({
        loading: false,
        data,
        error: !response.ok ? messageFromData(data, `请求失败：${response.status}`) : undefined
      });
      if (response.ok) applyHistory(data);
    } catch (error) {
      setResult({ loading: false, error: error instanceof Error ? error.message : "请求失败" });
    }
  }

  function buildSubmitPayload() {
    if (mode === "single") {
      const cleanCdkey = cdkey.trim();
      const cleanToken = normalizeAccessToken(accessToken);
      if (!cleanCdkey || !cleanToken) return { error: "CDKey 和 access_token 都不能为空" };
      return {
        payload: {
          items: [{ cdkey: cleanCdkey, access_token: cleanToken }]
        }
      };
    }

    const lines = batch.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    if (!lines.length) return { error: "请先粘贴批量兑换内容" };
    if (lines.length > 20) return { error: "单次最多提交 20 条" };
    const items: Array<{ cdkey: string; access_token: string }> = [];
    for (const line of lines) {
      const [rawCdkey, rawToken] = line.split("----");
      const cleanCdkey = rawCdkey?.trim() || "";
      const cleanToken = normalizeAccessToken(rawToken || "");
      if (!cleanCdkey || !cleanToken) return { error: `格式错误：${line}` };
      items.push({ cdkey: cleanCdkey, access_token: cleanToken });
    }
    const duplicate = validateUnique(items.map((item) => item.cdkey));
    if (duplicate) return { error: `重复 CDKey：${duplicate}` };
    return { payload: { items } };
  }

  function handleRedeem(event: FormEvent) {
    event.preventDefault();
    const built = buildSubmitPayload();
    if ("error" in built) {
      setResult({ loading: false, error: built.error });
      return;
    }
    submit("/redeem-api/cdkey-redeems", built.payload);
  }

  function handleStatus(event: FormEvent) {
    event.preventDefault();
    const cdkeys = statusKeys.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    if (!cdkeys.length) {
      setResult({ loading: false, error: "请至少输入一个 CDKey" });
      return;
    }
    if (cdkeys.length > 100) {
      setResult({ loading: false, error: "单次最多查询 100 条" });
      return;
    }
    const duplicate = validateUnique(cdkeys);
    if (duplicate) {
      setResult({ loading: false, error: `重复 CDKey：${duplicate}` });
      return;
    }
    submit("/redeem-api/cdkey-redeems/status", { cdkeys });
  }

  return (
    <main className="redeemPage">
      <header className="redeemHero">
        <div className="redeemBrand">
          <img src="/brand-logo.png" alt="AI华强北" className="redeemLogo" />
          <div>
            <p className="eyebrow">AI华强北 · CDKey Redeem</p>
            <h1>AI华强北 CDK 兑换</h1>
            <p className="lead">低价 · 稳定 · 批发，提交 CDKey 完成兑换。</p>
          </div>
        </div>
        <a className="backLink" href="/">
          返回工具台
        </a>
      </header>

      <section className="panel redeemPanel">
        <div className="redeemCardHeader">
          <div>
            <p className="redeemKicker">AI华强北兑换中心</p>
            <h2>提交兑换信息</h2>
          </div>
          <img src="/brand-logo.png" alt="" className="redeemMark" />
        </div>

        <QueueCards queue={queue} loading={queueLoading} error={queueError} />

        <div className="seg redeemSeg">
          <button type="button" className={mode === "single" ? "active" : ""} onClick={() => setMode("single")}>
            单条兑换
          </button>
          <button type="button" className={mode === "batch" ? "active" : ""} onClick={() => setMode("batch")}>
            批量兑换
          </button>
          <button type="button" className={mode === "status" ? "active" : ""} onClick={() => setMode("status")}>
            状态查询
          </button>
        </div>

        {mode !== "status" ? (
          <form className="grid redeemForm" onSubmit={handleRedeem}>
            {mode === "single" ? (
              <>
                <label className="field">
                  <span>CDKey</span>
                  <input value={cdkey} onChange={(event) => setCdkey(event.target.value)} placeholder="CDK-001" />
                </label>
                <label className="field">
                  <span>Access Token</span>
                  <input
                    value={accessToken}
                    onChange={(event) => setAccessToken(event.target.value)}
                    placeholder="粘贴 access_token 或 session JSON"
                    type="password"
                  />
                </label>
              </>
            ) : (
              <label className="field span2">
                <span>批量内容</span>
                <textarea
                  rows={10}
                  value={batch}
                  onChange={(event) => setBatch(event.target.value)}
                  placeholder={'CDK-001----ACCESS_TOKEN_1\nCDK-002----{"accessToken":"..."}'}
                />
              </label>
            )}
            <div className="actions span2">
              <button className="button primary" type="submit" disabled={result.loading}>
                {result.loading ? "提交中..." : "提交兑换"}
              </button>
              <button className="button ghost" type="button" onClick={() => setResult(emptyResult)}>
                清空结果
              </button>
              <p className="tokenHelp">
                Access Token在此处获取：
                <a href="https://chatgpt.com/api/auth/session" target="_blank" rel="noreferrer">
                  chatgpt.com/api/auth/session
                </a>
              </p>
            </div>
          </form>
        ) : (
          <form className="grid redeemForm" onSubmit={handleStatus}>
            <label className="field span2">
              <span>CDKey 列表</span>
              <textarea
                rows={10}
                value={statusKeys}
                onChange={(event) => setStatusKeys(event.target.value)}
                placeholder={"CDK-001\nCDK-002"}
              />
            </label>
            <div className="actions span2">
              <button className="button primary" type="submit" disabled={result.loading}>
                {result.loading ? "查询中..." : "查询状态"}
              </button>
              <button className="button ghost" type="button" onClick={() => setResult(emptyResult)}>
                清空结果
              </button>
            </div>
          </form>
        )}

        <section className="redeemTools" aria-label="辅助功能">
          <a className="toolCard" href="https://code.hasdg.fun/" target="_blank" rel="noreferrer">
            <span>邮箱验证码获取</span>
            <strong>打开验证码站点</strong>
            <small>新标签页跳转，不影响当前兑换页面</small>
          </a>
          <a className="toolCard accent" href="/?tab=subscription">
            <span>Plus 状态查询</span>
            <strong>进入订阅查询</strong>
            <small>复用现有订阅状态查询能力</small>
          </a>
        </section>

        <ResultView result={result} />
      </section>

      <ProgressPanel history={history} pollState={pollState} />
      <HistoryPanel history={history} />
    </main>
  );
}
